/**
 * Monte Carlo simulation engine.
 * Pure JS — no React, no network calls. All randomness, all computation.
 *
 * Takes a bootstrap payload from the backend and runs N simulations of
 * the remaining dynasty season. Returns probability distributions for
 * playoff odds, win totals, championship odds, pick slot probabilities,
 * and max points for.
 */

const NUM_SIMULATIONS = 10000;

// ─── Public API ────────────────────────────────────────────────────────────

/**
 * Run the full simulation from a bootstrap payload.
 * @param {object} bootstrap - from /api/monte-carlo/league/:id/bootstrap
 * @param {object} options
 * @param {number} [options.numSimulations=10000]
 * @param {object} [options.teamModes] - { [rosterId]: 'competing' | 'tanking' }
 * @param {Array}  [options.tradeAdjustments] - list of trade swap objects
 * @returns {SimulationResults}
 */
export function runSimulation(bootstrap, options = {}) {
  const {
    numSimulations = NUM_SIMULATIONS,
    teamModes = {},
    tradeAdjustments = [],
  } = options;

  // Apply any trade adjustments to a local copy (never mutates bootstrap)
  const adjustedBootstrap = tradeAdjustments.length > 0
    ? applyTradeAdjustments(bootstrap, tradeAdjustments)
    : bootstrap;

  const {
    teams,
    standings,
    schedule,
    currentWeek,
    regularSeasonWeeks,
    playoffTeams,
    playerDistributions,
    rosterPositions,
  } = adjustedBootstrap;

  // Pre-build per-team scoring parameter arrays (avoids recomputing every sim)
  const teamScoringParams = buildTeamScoringParams(
    teams,
    playerDistributions,
    rosterPositions,
    teamModes
  );

  // The week-by-week fan chart only needs a representative sample — tracking every
  // sim's per-week probability would cost ~67MB at 100k sims. Cap it so high sim
  // counts stay fast and light while headline odds still use all N simulations.
  const weeklySample = Math.min(numSimulations, 4000);

  // Accumulator arrays (one element per simulation per team)
  const rosterIds = teams.map((t) => t.rosterId);
  const accumulators = {};
  for (const id of rosterIds) {
    accumulators[id] = {
      wins: new Float32Array(numSimulations),
      pointsFor: new Float32Array(numSimulations),
      maxPointsFor: new Float32Array(numSimulations),
      playoffCount: 0,
      semiCount: 0,
      finalsCount: 0,
      champCount: 0,
      pickSlots: [],
      // How often this team lands in each draft slot (0-indexed: slot 0 = pick 1.01).
      draftSlotCounts: new Float32Array(rosterIds.length),
      // Per-week playoff probability for the fan chart (capped sample).
      weeklyPlayoff: Array.from({ length: regularSeasonWeeks }, () => new Float32Array(weeklySample)),
    };
  }

  // All traded-pick outcomes across simulations
  // key = "season:round:originalRosterId" → array of winning team's final rank per sim
  const picksTracked = collectTradedPicks(teams);
  const pickOutcomes = {};
  for (const key of Object.keys(picksTracked)) {
    pickOutcomes[key] = new Int8Array(numSimulations);
  }

  // In the offseason (week 0), always simulate the full season from scratch.
  // Using prior-year standings would inflate wins (e.g. 9 existing + 14 sim = 23).
  const isOffseason = !currentWeek || currentWeek === 0;
  const firstSimWeek = isOffseason ? 1 : Math.max(currentWeek + 1, 1);

  // ── Main simulation loop ──────────────────────────────────────────────
  for (let sim = 0; sim < numSimulations; sim++) {
    // Start from real standings in-season; start fresh in the offseason
    const wins = {};
    const losses = {};
    const pf = {};
    for (const id of rosterIds) {
      const s = (!isOffseason && standings[id]) ? standings[id] : {};
      wins[id] = s.wins || 0;
      losses[id] = s.losses || 0;
      pf[id] = s.pointsFor || 0;
    }

    // Simulate remaining regular season weeks
    for (let week = firstSimWeek; week <= regularSeasonWeeks; week++) {
      const matchups = (schedule[week] || []);
      for (const { rosterIds: [a, b] } of matchups) {
        if (a === undefined || b === undefined) continue;
        const scoreA = sampleTeamScore(teamScoringParams[a]);
        const scoreB = sampleTeamScore(teamScoringParams[b]);
        pf[a] = (pf[a] || 0) + scoreA;
        pf[b] = (pf[b] || 0) + scoreB;
        if (scoreA >= scoreB) { wins[a]++; losses[b]++; }
        else { wins[b]++; losses[a]++; }
      }

      // Fan-chart tracking only runs for the capped sample (keeps 100k sims fast).
      if (sim < weeklySample) {
        const weekRank = seedTeams(rosterIds, wins, pf);
        const weeksElapsed = week - firstSimWeek + 1;
        const totalWeeks = regularSeasonWeeks - firstSimWeek + 1;
        const progress = Math.min(1, weeksElapsed / totalWeeks);

        for (const id of rosterIds) {
          const rank = weekRank.indexOf(id); // 0-indexed
          const inPlayoffs = rank < playoffTeams;
          // Rank-based certainty: top seed → ~95%, cutline → ~50%, bottom → ~5%
          const rankPct = inPlayoffs
            ? 95 - (rank / (playoffTeams - 1)) * 45
            : 45 - ((rank - playoffTeams) / (rosterIds.length - playoffTeams)) * 40;
          // Blend from aggregate odds toward rank-based certainty as season progresses
          const priorOdds = accumulators[id].playoffCount / Math.max(1, sim) * 100;
          const blended = priorOdds + (rankPct - priorOdds) * progress;
          accumulators[id].weeklyPlayoff[week - 1][sim] = Math.max(1, Math.min(99, blended));
        }
      }
    }

    // Seed teams by record (wins desc, tiebreak: pf desc)
    const seeds = seedTeams(rosterIds, wins, pf);
    const playoffBracket = seeds.slice(0, playoffTeams);

    for (const id of playoffBracket) accumulators[id].playoffCount++;

    // Simulate single-elimination playoff bracket
    const { champion, finalists, semifinalists } = simulatePlayoffs(playoffBracket, teamScoringParams);
    if (champion !== null) accumulators[champion].champCount++;
    for (const id of finalists) if (accumulators[id]) accumulators[id].finalsCount++;
    for (const id of semifinalists) if (accumulators[id]) accumulators[id].semiCount++;

    // Store per-sim values
    for (let i = 0; i < rosterIds.length; i++) {
      const id = rosterIds[i];
      accumulators[id].wins[sim] = wins[id];
      accumulators[id].pointsFor[sim] = pf[id];
      // maxPointsFor is the same as pf here since we already simulate optimal lineups
      accumulators[id].maxPointsFor[sim] = pf[id];
    }

    // Draft order = reverse of FINAL standings (champion picks last). Non-playoff
    // teams pick first (worst regular season → 1.01); playoff teams follow in
    // reverse playoff finish (first eliminated next … champion last). This makes
    // the last pick track the championship, not just the best regular season.
    const inBracket = new Set(playoffBracket);
    const finalistSet = new Set(finalists);
    const semiSet = new Set(semifinalists);
    const finishDepth = (id) => {
      if (id === champion) return 4;      // wins it all → picks last
      if (finalistSet.has(id)) return 3;  // lost the final
      if (semiSet.has(id)) return 2;      // lost the semis
      return 1;                           // made playoffs, out earlier
    };
    const regCmp = (a, b) => (wins[a] - wins[b]) || ((pf[a] || 0) - (pf[b] || 0)); // worst first
    const nonPlayoff = rosterIds.filter((id) => !inBracket.has(id)).sort(regCmp);
    const playoffOrdered = [...playoffBracket].sort((a, b) => (finishDepth(a) - finishDepth(b)) || regCmp(a, b));
    const draftOrder = [...nonPlayoff, ...playoffOrdered];

    // Tally each team's draft slot for this sim (slot 0 = pick 1.01).
    for (let slot = 0; slot < draftOrder.length; slot++) {
      accumulators[draftOrder[slot]].draftSlotCounts[slot] += 1;
    }

    for (const [pickKey, info] of Object.entries(picksTracked)) {
      // The pick belongs to whoever owns it — find their slot in the draft order
      const ownerRosterId = info.ownerRosterId;
      const slot = draftOrder.indexOf(ownerRosterId) + 1; // 1-indexed
      if (pickOutcomes[pickKey]) pickOutcomes[pickKey][sim] = slot;
    }
  }

  // ── Aggregate results ─────────────────────────────────────────────────
  const teamResults = {};
  for (const id of rosterIds) {
    const acc = accumulators[id];
    teamResults[id] = {
      playoffProbability: acc.playoffCount / numSimulations,
      semifinalProbability: acc.semiCount / numSimulations,
      finalsProbability: acc.finalsCount / numSimulations,
      championshipProbability: acc.champCount / numSimulations,
      wins: percentiles(acc.wins),
      winHistogram: winHistogram(acc.wins, regularSeasonWeeks),
      pointsFor: percentiles(acc.pointsFor),
      maxPointsFor: percentiles(acc.maxPointsFor),
      // Per-week playoff probability percentiles for the fan chart
      weeklyPlayoff: acc.weeklyPlayoff.map(weekArr => percentiles(weekArr)),
      // Probability this team lands in each draft slot (index 0 = pick 1.01).
      draftSlotProbs: Array.from(acc.draftSlotCounts, c => c / numSimulations),
    };
  }

  const pickResults = {};
  for (const [key, slots] of Object.entries(pickOutcomes)) {
    pickResults[key] = {
      expectedSlot: mean(slots),
      distribution: slotDistribution(slots, teams.length),
      info: picksTracked[key],
    };
  }

  return { teams: teamResults, picks: pickResults, numSimulations };
}

/**
 * Apply trade adjustments to a bootstrap payload.
 * Returns a new bootstrap — the original is never mutated.
 */
export function applyTradeAdjustments(bootstrap, tradeAdjustments) {
  if (!tradeAdjustments || tradeAdjustments.length === 0) return bootstrap;

  // Deep-ish clone of teams only (playerDistributions/schedule untouched)
  const newTeams = bootstrap.teams.map((t) => ({
    ...t,
    players: [...(t.players || [])],
    starters: [...(t.starters || [])],
    picks: [...(t.picks || [])],
  }));

  const teamByRosterId = {};
  for (const t of newTeams) teamByRosterId[t.rosterId] = t;

  for (const trade of tradeAdjustments) {
    const { giveAway = [], receive = [], picksGiven = [], picksReceived = [] } = trade;

    // Move players
    for (const { playerId, fromRosterId, toRosterId } of [...giveAway, ...receive]) {
      if (!playerId || !fromRosterId || !toRosterId) continue;
      const from = teamByRosterId[fromRosterId];
      const to = teamByRosterId[toRosterId];
      if (!from || !to) continue;
      from.players = from.players.filter((id) => id !== playerId);
      if (!to.players.includes(playerId)) to.players.push(playerId);
    }

    // Move picks
    for (const pick of [...picksGiven, ...picksReceived]) {
      const from = teamByRosterId[pick.fromRosterId];
      const to = teamByRosterId[pick.toRosterId];
      if (!from || !to) continue;
      from.picks = from.picks.filter(
        (p) => !(p.season === pick.season && p.round === pick.round && p.originalRosterId === pick.originalRosterId)
      );
      to.picks.push({ ...pick, ownerRosterId: to.rosterId });
    }
  }

  return { ...bootstrap, teams: newTeams };
}

/**
 * Compute KTC delta for a proposed trade.
 * Returns { given, received, delta } where delta = received - given.
 */
export function computeKTCDelta(tradeAdjustments, playerDistributions, isSuperFlex) {
  const valueKey = isSuperFlex ? 'ktcValueSF' : 'ktcValue1QB';
  let given = 0;
  let received = 0;

  for (const trade of tradeAdjustments) {
    for (const { playerId } of trade.giveAway || []) {
      given += playerDistributions[playerId]?.[valueKey] || 0;
    }
    for (const pick of trade.picksGiven || []) {
      given += pick.ktcValue || 0;
    }
    for (const { playerId } of trade.receive || []) {
      received += playerDistributions[playerId]?.[valueKey] || 0;
    }
    for (const pick of trade.picksReceived || []) {
      received += pick.ktcValue || 0;
    }
  }

  return { given, received, delta: received - given };
}

// ─── Internal helpers ──────────────────────────────────────────────────────

/**
 * Build per-team scoring parameters: an array of { mean, stdDev } per starting slot.
 * We greedily assign the highest-projected available player to each starting position.
 * tanking teams get their means multiplied by 0.75.
 */
function buildTeamScoringParams(teams, playerDistributions, rosterPositions, teamModes) {
  const startingPositions = (rosterPositions || []).filter(
    (p) => !['BN', 'TAXI', 'IR'].includes(p)
  );

  const params = {};
  for (const team of teams) {
    // 'suboptimal' = 10% reduction, modeling real-world lineup mistakes, not full tanking
    const multiplier = teamModes[team.rosterId] === 'suboptimal' ? 0.90 : 1.0;
    const starters = assignOptimalLineup(team.players, playerDistributions, startingPositions);
    params[team.rosterId] = starters.map(({ mean, stdDev }) => ({
      mean: mean * multiplier,
      stdDev: stdDev * multiplier,
    }));
  }
  return params;
}

/**
 * Total projected weekly points of a team's optimal starting lineup.
 * Used to rank teams by strength (e.g. which contenders bid hardest for chips).
 */
export function teamLineupMean(players, distributions, rosterPositions) {
  const startingPositions = (rosterPositions || []).filter(
    (p) => !['BN', 'TAXI', 'IR'].includes(p)
  );
  const lineup = assignOptimalLineup(players, distributions, startingPositions);
  return lineup.reduce((sum, slot) => sum + (slot.mean || 0), 0);
}

/**
 * Greedily assign players to positions to maximize total projected score.
 * Returns array of { mean, stdDev } for each starting slot.
 */
function assignOptimalLineup(players, distributions, startingPositions) {
  if (!players || !startingPositions || startingPositions.length === 0) return [];

  // Build list of available players with their projected scores
  const available = [];
  for (const sleeperId of players) {
    const dist = distributions[sleeperId];
    if (!dist || dist.meanWeeklyPoints <= 0) continue;
    available.push({ sleeperId, ...dist });
  }

  // Sort by mean descending for greedy assignment
  available.sort((a, b) => b.meanWeeklyPoints - a.meanWeeklyPoints);

  const used = new Set();
  const lineup = [];

  // Define which NFL positions can fill each fantasy slot
  const SLOT_ELIGIBILITY = {
    QB: ['QB'],
    RB: ['RB'],
    WR: ['WR'],
    TE: ['TE'],
    FLEX: ['RB', 'WR', 'TE'],
    SUPER_FLEX: ['QB', 'RB', 'WR', 'TE'],
    K: ['K'],
    DEF: ['DEF'],
    IDP_FLEX: ['LB', 'DL', 'DB'],
  };

  for (const slot of startingPositions) {
    const eligible = SLOT_ELIGIBILITY[slot] || [slot];
    const player = available.find(
      (p) => !used.has(p.sleeperId) && eligible.includes(p.position)
    );
    if (player) {
      used.add(player.sleeperId);
      lineup.push({ mean: player.meanWeeklyPoints, stdDev: player.stdDev });
    } else {
      lineup.push({ mean: 0, stdDev: 0 }); // empty slot
    }
  }

  return lineup;
}

/**
 * Sample a team's total weekly score from its starting lineup.
 */
function sampleTeamScore(scoringParams) {
  if (!scoringParams) return 0;
  let total = 0;
  for (const { mean, stdDev } of scoringParams) {
    total += sampleNormal(mean, stdDev);
  }
  return total;
}

/**
 * Box-Muller normal sample, floored at 0.
 */
function sampleNormal(mean, stdDev) {
  if (stdDev <= 0) return Math.max(0, mean);
  const u1 = Math.random();
  const u2 = Math.random();
  // Avoid log(0)
  const safe = u1 < 1e-10 ? 1e-10 : u1;
  const z = Math.sqrt(-2 * Math.log(safe)) * Math.cos(2 * Math.PI * u2);
  return Math.max(0, mean + z * stdDev);
}

/**
 * Sort teams by wins desc, tiebreak by points-for desc.
 * Returns array of rosterIds in seed order (index 0 = #1 seed).
 */
function seedTeams(rosterIds, wins, pf) {
  return [...rosterIds].sort((a, b) => {
    const wDiff = (wins[b] || 0) - (wins[a] || 0);
    if (wDiff !== 0) return wDiff;
    return (pf[b] || 0) - (pf[a] || 0);
  });
}

/**
 * Simulate a standard seeded single-elimination bracket with first-round byes.
 *
 * The top `byes` seeds (byes = nextPow2(N) - N) sit out round 1; the rest play
 * highest-vs-lowest. Every later round re-seeds (highest remaining seed plays the
 * lowest) until a champion remains — the format the vast majority of fantasy
 * leagues use.
 *
 * Returns { champion, finalists, semifinalists } where finalists are the two
 * teams in the title game and semifinalists are everyone who reached the round
 * before it.
 */
function simulatePlayoffs(bracket, teamScoringParams) {
  if (!bracket || bracket.length === 0) return { champion: null, finalists: [], semifinalists: [] };
  if (bracket.length === 1) return { champion: bracket[0], finalists: [], semifinalists: [] };

  // `remaining` stays in seed order so each round naturally re-seeds.
  let remaining = [...bracket];
  const rounds = []; // rounds[i] = teams that entered round i (seed-ordered)

  // ── Round 1: top seeds get byes, the rest play highest-vs-lowest ──
  rounds.push([...remaining]);
  let pow2 = 1;
  while (pow2 < remaining.length) pow2 *= 2;
  const byes = pow2 - remaining.length;

  const advanced = remaining.slice(0, byes);
  const playing = remaining.slice(byes);
  for (let i = 0; i < playing.length / 2; i++) {
    const a = playing[i];
    const b = playing[playing.length - 1 - i];
    advanced.push(sampleTeamScore(teamScoringParams[a]) >= sampleTeamScore(teamScoringParams[b]) ? a : b);
  }
  // Re-seed survivors back into seed order for the next round.
  const seedRank = new Map(bracket.map((id, i) => [id, i]));
  remaining = advanced.sort((x, y) => seedRank.get(x) - seedRank.get(y));

  // ── Remaining rounds are clean powers of two ──
  while (remaining.length > 1) {
    rounds.push([...remaining]);
    const next = [];
    for (let i = 0; i < remaining.length / 2; i++) {
      const a = remaining[i];
      const b = remaining[remaining.length - 1 - i];
      next.push(sampleTeamScore(teamScoringParams[a]) >= sampleTeamScore(teamScoringParams[b]) ? a : b);
    }
    remaining = next;
  }

  const champion = remaining[0] ?? null;
  const finalists = rounds[rounds.length - 1] || [];
  const semifinalists = rounds.length >= 2 ? rounds[rounds.length - 2] : [];

  return { champion, finalists, semifinalists };
}

/**
 * Collect all traded picks (picks not owned by the team they originated from).
 * Returns map of pickKey → { season, round, originalRosterId, ownerRosterId }
 */
function collectTradedPicks(teams) {
  const tracked = {};
  for (const team of teams) {
    for (const pick of team.picks || []) {
      if (pick.originalRosterId !== team.rosterId) {
        const key = `${pick.season}:${pick.round}:${pick.originalRosterId}`;
        tracked[key] = {
          season: pick.season,
          round: pick.round,
          originalRosterId: pick.originalRosterId,
          ownerRosterId: team.rosterId,
          ownerName: team.displayName,
        };
      }
    }
  }
  return tracked;
}

// ─── Statistics helpers ────────────────────────────────────────────────────

function winHistogram(arr, maxWins) {
  const counts = new Array(maxWins + 1).fill(0);
  for (let i = 0; i < arr.length; i++) {
    const w = Math.min(Math.round(arr[i]), maxWins);
    if (w >= 0) counts[w]++;
  }
  const n = arr.length;
  return counts.map((count, wins) => ({ wins, prob: count / n }));
}

function percentiles(arr) {
  const sorted = Float32Array.from(arr).sort();
  const n = sorted.length;
  return {
    p10: sorted[Math.floor(n * 0.1)],
    p25: sorted[Math.floor(n * 0.25)],
    p50: sorted[Math.floor(n * 0.5)],
    p75: sorted[Math.floor(n * 0.75)],
    p90: sorted[Math.floor(n * 0.9)],
  };
}

function mean(arr) {
  let sum = 0;
  for (let i = 0; i < arr.length; i++) sum += arr[i];
  return Math.round((sum / arr.length) * 10) / 10;
}

function slotDistribution(arr, numTeams) {
  const counts = {};
  for (let i = 1; i <= numTeams; i++) counts[i] = 0;
  for (let i = 0; i < arr.length; i++) {
    const slot = arr[i];
    if (slot >= 1 && slot <= numTeams) counts[slot]++;
  }
  const n = arr.length;
  const dist = {};
  for (const [slot, count] of Object.entries(counts)) {
    dist[slot] = Math.round((count / n) * 1000) / 1000;
  }
  return dist;
}
